import React, { useState, useEffect, useRef, useCallback, Suspense, lazy } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useModal } from '../contexts/ModalContext';
import ForgotPasswordModal from './ForgotPasswordModal';
import { getRecaptchaSiteKey, isRecaptchaConfigured } from '../config/recaptcha';
import { verifyRecaptchaToken } from '../utils/verifyRecaptchaToken';
import { useCompanySettings } from '../hooks/useCompanySettings';
import { getSavedLogins, upsertSavedLogin, removeSavedLogin, isLoginFresh } from '../utils/savedLogins';
import { trackLogin } from '../services/analyticsService';

const ReCAPTCHA = lazy(() => import('react-google-recaptcha'));

const ERROR_FIELD_MAP = {
  'O nome é obrigatório': ['name'],
  'O telefone é obrigatório': ['phone'],
  'Telefone deve ter pelo menos 10 dígitos': ['phone'],
  'As senhas não coincidem': ['confirmPassword'],
  'A senha deve ter pelo menos 6 caracteres': ['password'],
  'Este email já está em uso': ['email'],
  'Email inválido': ['email'],
  'Senha incorreta': ['password'],
  'Usuário não encontrado': ['email'],
  'Sessão rápida expirada. Digite a senha novamente.': ['password'],
};

/**
 * @param {'modal' | 'page'} variant
 * @param {boolean} [isOpen] — obrigatório quando variant === 'modal'
 */
function LoginAuthForm({ message, variant = 'modal', onClose, isOpen: isOpenProp }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [googleUser, setGoogleUser] = useState(null);
  const [showGoogleForm, setShowGoogleForm] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [savedLogins, setSavedLogins] = useState(() => getSavedLogins());
  const [selectedSavedEmail, setSelectedSavedEmail] = useState('');
  const [rememberThisDevice, setRememberThisDevice] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  const [errorFields, setErrorFields] = useState([]);
  const [dismissedErrorFields, setDismissedErrorFields] = useState([]);
  const recaptchaRef = useRef(null);
  const googleSignupRecaptchaRef = useRef(null);
  const errorRef = useRef(null);

  const resetRecaptcha = useCallback(() => {
    recaptchaRef.current?.reset();
    googleSignupRecaptchaRef.current?.reset();
  }, []);

  const clearFormErrors = useCallback(() => {
    setError('');
    setErrorFields([]);
    setDismissedErrorFields([]);
  }, []);

  const setFormError = useCallback((message, fields = null) => {
    setError(message);
    setErrorFields(fields ?? ERROR_FIELD_MAP[message] ?? []);
    setDismissedErrorFields([]);
  }, []);

  const dismissFieldError = useCallback((field) => {
    setDismissedErrorFields((prev) => (prev.includes(field) ? prev : [...prev, field]));
  }, []);

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    const limitedNumbers = numbers.slice(0, 11);
    if (limitedNumbers.length <= 2) {
      return limitedNumbers;
    } else if (limitedNumbers.length <= 7) {
      return `(${limitedNumbers.slice(0, 2)}) ${limitedNumbers.slice(2)}`;
    } else if (limitedNumbers.length <= 11) {
      return `(${limitedNumbers.slice(0, 2)}) ${limitedNumbers.slice(2, 7)}-${limitedNumbers.slice(7)}`;
    }
    return limitedNumbers;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setPhone(formatted);
  };
  const { login, signup, loginWithGoogle, completeGoogleSignup } = useAuth();
  const { recaptchaSiteKey: remoteRecaptchaSiteKey } = useCompanySettings();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';
  const { setModalOpen } = useModal();
  const effectiveRecaptchaSiteKey = getRecaptchaSiteKey(remoteRecaptchaSiteKey);

  const ensureRecaptchaVerified = useCallback(async (ref) => {
    if (!isRecaptchaConfigured(remoteRecaptchaSiteKey)) return;
    const r = ref || recaptchaRef;
    const token = r.current?.getValue();
    if (!token) {
      throw new Error('Marque a verificação "Não sou um robô" antes de continuar.');
    }
    await verifyRecaptchaToken(token);
  }, [remoteRecaptchaSiteKey]);

  useEffect(() => {
    if (variant === 'page') {
      setSavedLogins(getSavedLogins());
      return;
    }
    if (isOpenProp) {
      setIsAnimating(true);
      setModalOpen(true);
    } else {
      setIsAnimating(false);
      setModalOpen(false);
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setName('');
      setPhone('');
      setError('');
      setErrorFields([]);
      setDismissedErrorFields([]);
      setIsSignUp(false);
      setGoogleUser(null);
      setShowGoogleForm(false);
      setShowForgotPassword(false);
      setShowPassword(false);
      setConfirmPasswordTouched(false);
      resetRecaptcha();
    }
  }, [isOpenProp, setModalOpen, variant, resetRecaptcha]);

  useEffect(() => {
    if (!error || variant !== 'page') return;
    requestAnimationFrame(() => {
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [error, variant]);

  const finishClose = useCallback(() => {
    if (variant === 'modal' && onClose) onClose();
  }, [variant, onClose]);

  const loginAndRedirect = useCallback(
    async (targetEmail, targetPassword, persistLogin = true) => {
      const isAdmin = await login(targetEmail, targetPassword);
      trackLogin('email');

      if (rememberThisDevice && persistLogin && targetEmail) {
        setSavedLogins(upsertSavedLogin(targetEmail, { secret: targetPassword }));
      }

      if (isAdmin) {
        finishClose();
        navigate('/admin');
      } else {
        finishClose();
        navigate(from);
      }
    },
    [from, login, navigate, rememberThisDevice, finishClose]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    clearFormErrors();

    try {
      await ensureRecaptchaVerified(recaptchaRef);

      if (isSignUp) {
        if (!name.trim()) {
          setFormError('O nome é obrigatório');
          setIsLoading(false);
          return;
        }
        if (!phone.trim()) {
          setFormError('O telefone é obrigatório');
          setIsLoading(false);
          return;
        }
        const phoneNumbers = phone.replace(/\D/g, '');
        if (phoneNumbers.length < 10) {
          setFormError('Telefone deve ter pelo menos 10 dígitos');
          setIsLoading(false);
          return;
        }
        if (password !== confirmPassword) {
          setFormError('As senhas não coincidem');
          setIsLoading(false);
          return;
        }
        if (password.length < 6) {
          setFormError('A senha deve ter pelo menos 6 caracteres');
          setIsLoading(false);
          return;
        }

        await signup(email, password, name, phoneNumbers);
        finishClose();
        navigate(from);
      } else {
        await loginAndRedirect(email, password, true);
      }
    } catch (err) {
      console.error('Erro:', err);
      resetRecaptcha();
      if (err.message && !err.code) {
        setFormError(err.message);
      } else if (err.code === 'auth/email-already-in-use') {
        setFormError('Este email já está em uso');
      } else if (err.code === 'auth/invalid-email') {
        setFormError('Email inválido');
      } else if (err.code === 'auth/wrong-password') {
        setFormError('Senha incorreta');
      } else if (err.code === 'auth/user-not-found') {
        setFormError('Usuário não encontrado');
      } else {
        setFormError('Erro ao processar sua solicitação');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError('');

    try {
      await ensureRecaptchaVerified(recaptchaRef);

      const result = await loginWithGoogle();

      if (result.isNewUser) {
        setGoogleUser(result.user);
        setShowGoogleForm(true);
        setName(result.user.displayName || '');
        setEmail(result.user.email || '');
      } else {
        trackLogin('google');
        finishClose();
        if (result.isAdmin) {
          navigate('/admin');
        } else {
          navigate(from);
        }
      }
    } catch (err) {
      console.error('Erro no login com Google:', err);
      resetRecaptcha();
      if (err.message && !err.code) {
        setError(err.message);
      } else {
        setError('Erro ao fazer login com Google');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignupComplete = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await ensureRecaptchaVerified(googleSignupRecaptchaRef);

      if (!name.trim()) {
        setError('O nome é obrigatório');
        setIsLoading(false);
        return;
      }
      if (!phone.trim()) {
        setError('O telefone é obrigatório');
        setIsLoading(false);
        return;
      }

      const phoneNumbers = phone.replace(/\D/g, '');
      if (phoneNumbers.length < 10) {
        setError('Telefone deve ter pelo menos 10 dígitos');
        setIsLoading(false);
        return;
      }

      await completeGoogleSignup(googleUser.uid, name, phoneNumbers);
      finishClose();
      navigate(from);
    } catch (err) {
      console.error('Erro ao completar cadastro:', err);
      resetRecaptcha();
      if (err.message && !err.code) {
        setError(err.message);
      } else {
        setError('Erro ao completar cadastro');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (variant === 'modal' && !isOpenProp && !isAnimating) return null;

  const recaptchaBlock = isRecaptchaConfigured(remoteRecaptchaSiteKey) && (
    <div className={`flex justify-center ${variant === 'page' ? 'scale-[1.02] origin-top' : ''}`}>
      <Suspense fallback={<div className="h-[78px] w-[304px] rounded bg-gray-100 animate-pulse" aria-hidden="true" />}>
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={effectiveRecaptchaSiteKey}
          hl="pt-BR"
          theme="light"
          size="normal"
        />
      </Suspense>
    </div>
  );

  const googleSignupRecaptchaBlock = isRecaptchaConfigured(remoteRecaptchaSiteKey) && (
    <div className="flex justify-center">
      <Suspense fallback={<div className="h-[78px] w-[304px] rounded bg-gray-100 animate-pulse" aria-hidden="true" />}>
        <ReCAPTCHA
          ref={googleSignupRecaptchaRef}
          sitekey={effectiveRecaptchaSiteKey}
          hl="pt-BR"
          theme="light"
          size="normal"
        />
      </Suspense>
    </div>
  );

  const labelClass =
    variant === 'page'
      ? 'block text-xs font-semibold tracking-[0.1em] text-gray-600 uppercase mb-1.5'
      : 'block text-sm font-medium text-gray-700 mb-2';

  const inputClass =
    variant === 'page'
      ? 'w-full min-h-[52px] px-5 py-3.5 border border-gray-200/90 rounded-lg text-base bg-white text-gray-900 placeholder-gray-400 shadow-sm outline-none transition-colors focus:outline-none focus:ring-0'
      : 'w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-sm placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-colors';

  const inputWithIconClass =
    variant === 'page'
      ? 'w-full min-h-[52px] pl-10 pr-4 py-3.5 border border-gray-200/90 rounded-lg text-base bg-white placeholder-gray-400 shadow-sm outline-none transition-colors focus:outline-none focus:ring-0'
      : inputClass;

  const passwordToggleButton = (
    <button
      type="button"
      onClick={() => setShowPassword((prev) => !prev)}
      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
      aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
    >
      {showPassword ? (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
          />
        </svg>
      ) : (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
          />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      )}
    </button>
  );

  const phoneDigits = phone.replace(/\D/g, '');
  const isSignUpFormReady =
    name.trim().length > 0 &&
    phoneDigits.length >= 10 &&
    email.trim().length > 0 &&
    password.length >= 6 &&
    confirmPassword.length > 0 &&
    password === confirmPassword;

  const isLoginFormReady = email.trim().length > 0 && password.length > 0;
  const isSubmitReady = isSignUp ? isSignUpFormReady : isLoginFormReady;

  const showPasswordMismatch =
    isSignUp &&
    confirmPasswordTouched &&
    confirmPassword.length > 0 &&
    password !== confirmPassword;

  const fieldHasError = (field) => {
    if (dismissedErrorFields.includes(field)) return false;
    if (errorFields.includes(field)) return true;
    if (showPasswordMismatch && field === 'confirmPassword') return true;
    return false;
  };

  const withFieldBorder = (baseClass, field) =>
    fieldHasError(field) ? `${baseClass} !border-red-500` : baseClass;

  const handleConfirmPasswordBlur = () => {
    setConfirmPasswordTouched(true);
    if (confirmPassword.length > 0 && password !== confirmPassword) {
      setDismissedErrorFields((prev) => prev.filter((field) => field !== 'confirmPassword'));
    }
  };

  const passwordInputClass = (withLeftIcon = false, field = 'password') =>
    withFieldBorder(`${withLeftIcon ? inputWithIconClass : inputClass} pr-11`, field);

  const formInner = (
    <>
      {message && (
        <div className="bg-orange-50 border border-orange-200 text-orange-700 px-4 py-3 rounded-lg mb-6 flex items-start gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-5 h-5 flex-shrink-0 mt-0.5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
            />
          </svg>
          <div className="text-sm">{message}</div>
        </div>
      )}

      {error && (
        <div
          ref={errorRef}
          role="alert"
          className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6 scroll-mt-6"
        >
          {error}
        </div>
      )}

      {!isRecaptchaConfigured(remoteRecaptchaSiteKey) && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg mb-6">
          <p className="text-sm">
            Verificacao anti-robo desativada: configure a Site Key do reCAPTCHA no painel Master.
          </p>
        </div>
      )}

      {variant === 'page' && !showGoogleForm && !isSignUp && savedLogins.length > 0 && (
        <div className="mb-7">
          <p className="text-xs font-semibold tracking-[0.1em] text-gray-600 uppercase mb-3">Contas salvas</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {savedLogins.slice(0, 2).map((acc) => {
              const fresh = isLoginFresh(acc.lastAuthAt);
              const canAutoLogin = fresh && Boolean(acc.secret);
              const isSelected = (selectedSavedEmail || '').toLowerCase() === acc.email;
              const displayName = acc.label && acc.label.trim().length > 0 ? acc.label.trim() : acc.email;

              const handleSelectSavedLogin = async () => {
                setSelectedSavedEmail(acc.email);
                setEmail(acc.email);
                setPassword('');
                clearFormErrors();

                if (!canAutoLogin) return;

                try {
                  setIsLoading(true);
                  await loginAndRedirect(acc.email, acc.secret, true);
                } catch (err) {
                  console.error('Erro no login rápido:', err);
                  setFormError('Sessão rápida expirada. Digite a senha novamente.');
                } finally {
                  setIsLoading(false);
                }
              };

              return (
                <div
                  key={acc.email}
                  role="button"
                  tabIndex={0}
                  onClick={handleSelectSavedLogin}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter' || ev.key === ' ') {
                      ev.preventDefault();
                      handleSelectSavedLogin();
                    }
                  }}
                  className={`text-left p-4 rounded-xl border transition-colors bg-white/70 hover:bg-white cursor-pointer ${
                    isSelected ? 'border-primary' : 'border-gray-200/90'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-900 truncate flex items-center gap-2">
                        <span className={`inline-block w-2.5 h-2.5 rounded-full ${canAutoLogin ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                        <span className="truncate">{displayName}</span>
                      </div>
                      <div className="mt-1 text-xs text-gray-600 truncate">{acc.email}</div>
                    </div>
                    <button
                      type="button"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        setSavedLogins(removeSavedLogin(acc.email));
                        if ((selectedSavedEmail || '').toLowerCase() === acc.email) {
                          setSelectedSavedEmail('');
                          setEmail('');
                        }
                      }}
                      className="shrink-0 w-9 h-9 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-500 flex items-center justify-center"
                      title="Remover"
                      aria-label={`Remover conta ${acc.email}`}
                    >
                      ×
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showGoogleForm ? (
        <form onSubmit={handleGoogleSignupComplete} className={variant === 'page' ? 'space-y-5' : 'space-y-4'}>
          <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg mb-6">
            <p className="text-sm">
              <strong>Conta Google conectada!</strong> Complete seus dados para finalizar o cadastro.
            </p>
          </div>

          <div>
            <label className={labelClass}>Nome</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={inputWithIconClass}
                placeholder="Seu nome completo"
                required
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Telefone</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
              </div>
              <input
                type="tel"
                value={phone}
                onChange={handlePhoneChange}
                className={inputWithIconClass}
                placeholder="(11) 99999-9999"
                maxLength={15}
                required
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Email</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                  />
                </svg>
              </div>
              <input
                type="email"
                value={email}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-sm bg-gray-50 text-gray-600"
                readOnly
              />
            </div>
          </div>

          {googleSignupRecaptchaBlock && (
            <div className="pt-2 pb-1">{googleSignupRecaptchaBlock}</div>
          )}

          <div className="mt-6">
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 px-4 text-white rounded-lg font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: 'var(--primary-color)',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'var(--primary-color-hover)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'var(--primary-color)';
              }}
            >
              {isLoading ? 'Processando...' : 'Completar Cadastro'}
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          {variant === 'page' && isSignUp && (
            <h2 className="text-2xl font-bold text-gray-900 font-montserrat">Criar conta</h2>
          )}

          {isSignUp && (
            <div>
              <label className={labelClass}>Nome</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onFocus={() => dismissFieldError('name')}
                  className={withFieldBorder(inputWithIconClass, 'name')}
                  placeholder="Seu nome completo"
                  required
                />
              </div>
            </div>
          )}

          {isSignUp && (
            <div>
              <label className={labelClass}>Telefone</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                </div>
                <input
                  type="tel"
                  value={phone}
                  onChange={handlePhoneChange}
                  onFocus={() => dismissFieldError('phone')}
                  className={withFieldBorder(inputWithIconClass, 'phone')}
                  placeholder="(11) 99999-9999"
                  maxLength={15}
                  required
                />
              </div>
            </div>
          )}

          <div>
            <label className={labelClass}>{variant === 'page' && !isSignUp ? 'Endereço de e-mail' : 'Email'}</label>
            {variant === 'page' && !isSignUp ? (
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => dismissFieldError('email')}
                className={withFieldBorder(inputClass, 'email')}
                placeholder="exemplo@dominio.com"
                required
                autoComplete="email"
                disabled={Boolean(selectedSavedEmail)}
              />
            ) : (
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                    />
                  </svg>
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => dismissFieldError('email')}
                  className={withFieldBorder(inputWithIconClass, 'email')}
                  placeholder={variant === 'page' ? 'exemplo@dominio.com' : 'Seu email'}
                  required
                />
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between gap-3">
              <label className={`${labelClass} mb-0`}>Senha</label>
              {!isSignUp && variant === 'page' && (
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.08em] shrink-0"
                  style={{ color: 'var(--primary-color)' }}
                >
                  Esqueceu a senha?
                </button>
              )}
            </div>
            {variant === 'page' && !isSignUp ? (
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => dismissFieldError('password')}
                  className={passwordInputClass(false, 'password')}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
                {passwordToggleButton}
              </div>
            ) : (
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => dismissFieldError('password')}
                  className={passwordInputClass(true, 'password')}
                  placeholder="••••••••"
                  required
                  autoComplete={isSignUp ? 'new-password' : 'current-password'}
                />
                {passwordToggleButton}
              </div>
            )}
            {!isSignUp && variant === 'modal' && (
              <div className="flex justify-end mt-2">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Esqueci minha senha
                </button>
              </div>
            )}
          </div>

          {isSignUp && (
            <div>
              <label className={labelClass}>Confirmar Senha</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onFocus={() => dismissFieldError('confirmPassword')}
                  onBlur={handleConfirmPasswordBlur}
                  className={passwordInputClass(true, 'confirmPassword')}
                  placeholder="Confirme sua senha"
                  required
                  autoComplete="new-password"
                />
                {passwordToggleButton}
              </div>
              {showPasswordMismatch && (
                <p className="mt-1.5 text-sm text-red-600" role="alert">
                  As senhas não coincidem
                </p>
              )}
            </div>
          )}

          {recaptchaBlock && (
            <div className={variant === 'page' ? 'pt-1 pb-1' : 'pt-2 pb-1'}>{recaptchaBlock}</div>
          )}

          {variant === 'page' && !isSignUp && !showGoogleForm && (
            <label className="flex items-center gap-3 text-sm text-gray-600 select-none">
              <input
                type="checkbox"
                className="rounded border-gray-300"
                checked={rememberThisDevice}
                onChange={(e) => setRememberThisDevice(e.target.checked)}
              />
              Salvar este login neste dispositivo
            </label>
          )}

          <div className={variant === 'page' ? 'pt-1' : 'pt-2'}>
            <button
              type="submit"
              disabled={isLoading || !isSubmitReady}
              className={`w-full text-white font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-montserrat ${variant === 'page' ? 'min-h-[54px] rounded-lg px-5 py-3.5 text-base shadow-sm' : 'rounded-lg py-3.5 px-4 text-sm'}`}
              style={{
                backgroundColor: 'var(--primary-color)',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'var(--primary-color-hover)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'var(--primary-color)';
              }}
            >
              {isLoading
                ? (!isSignUp && !showGoogleForm ? 'Entrando...' : 'Processando...')
                : isSignUp
                  ? 'Criar Conta'
                  : variant === 'page'
                    ? 'Entrar no Sistema'
                    : 'Login'}
            </button>
          </div>

          <div className="text-center">
            <span className="text-sm text-gray-600">{isSignUp ? 'Já tem uma conta? ' : 'Não tem uma conta? '}</span>
            <button
              type="button"
              onClick={() => {
                clearFormErrors();
                setIsSignUp(!isSignUp);
                setConfirmPassword('');
                setConfirmPasswordTouched(false);
                resetRecaptcha();
              }}
              className="text-sm font-medium transition-colors"
              style={{
                color: 'var(--primary-color)',
              }}
              onMouseEnter={(e) => {
                e.target.style.color = 'var(--primary-color-hover)';
              }}
              onMouseLeave={(e) => {
                e.target.style.color = 'var(--primary-color)';
              }}
            >
              {isSignUp ? 'Faça login' : 'Registre-se'}
            </button>
          </div>

          <div className={`relative ${variant === 'page' ? 'pt-3' : 'pt-2'}`}>
            <div className="absolute inset-0 flex items-center">
              <div className={`w-full border-t ${variant === 'page' ? 'border-gray-200/80' : 'border-gray-200'}`} />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className={`px-3 text-gray-400 uppercase tracking-[0.12em] text-[10px] font-semibold ${variant === 'page' ? 'bg-[#F4F7FE]' : 'bg-white'}`}>
                {isSignUp ? 'Ou faça registro com' : 'Ou continue com'}
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={isLoading}
            className={`w-full border text-gray-700 bg-white hover:bg-gray-50/80 font-medium transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed ${variant === 'page' ? 'min-h-[52px] rounded-lg border-gray-200/90 px-5 py-3.5 text-base shadow-sm' : 'rounded-lg border-gray-200 py-3 px-4 text-sm'}`}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            <span>{isLoading ? 'Processando...' : 'Google'}</span>
          </button>
        </form>
      )}
    </>
  );

  if (variant === 'page') {
    return (
      <>
        {formInner}
        <ForgotPasswordModal isOpen={showForgotPassword} onClose={() => setShowForgotPassword(false)} />
      </>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => onClose && onClose()}>
      <div
        className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-all duration-200 ${
          isOpenProp ? 'opacity-100' : 'opacity-0'
        }`}
      />

      <div
        className={`
          bg-white w-full h-full relative z-50 rounded-lg
          sm:rounded-2xl sm:max-w-md sm:mx-4 sm:h-auto
          flex flex-col
          transition-all duration-200
          ${isOpenProp ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
        `}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="rounded-t-2xl flex justify-between items-center p-6 pb-4 pt-4 bg-white border-b border-gray-200 sticky top-0 z-10">
          <h2 className="text-2xl font-bold text-gray-900">
            {showGoogleForm ? 'Completar Cadastro' : isSignUp ? 'Criar Conta' : 'Login'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-gray-500"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 pt-4">{formInner}</div>
      </div>

      <ForgotPasswordModal isOpen={showForgotPassword} onClose={() => setShowForgotPassword(false)} />
    </div>
  );
}

export default LoginAuthForm;
